import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FlaskConical, Plus, Pause, Play, CheckCircle2, BarChart3, GitBranch, Clock, TrendingUp, AlertCircle, ShieldCheck, ShieldAlert, ArrowRight, Zap, Lock, Trophy, Eye, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { getToken } from "../lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;

function getAuthHeaders() {
  const token = getToken();
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

// ─── Metric Display Helpers ──────────────────────────────────

function formatMetric(value, type = "number") {
  if (value === null || value === undefined) return "—";
  if (type === "currency") return `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  if (type === "percent") return `${(Number(value) * 100).toFixed(1)}%`;
  if (type === "ratio") return Number(value).toFixed(2);
  return Number(value).toLocaleString();
}

function MetricCard({ label, value, type = "number", sublabel, testId }) {
  const isEmpty = value === null || value === undefined || value === 0;
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4" data-testid={testId}>
      <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-2xl font-semibold ${isEmpty ? "text-zinc-600" : "text-zinc-100"}`}>
        {isEmpty ? "—" : formatMetric(value, type)}
      </p>
      {sublabel && <p className="text-xs text-zinc-500 mt-1">{sublabel}</p>}
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    paused: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    completed: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium rounded-full border ${colors[status] || colors.active}`} data-testid={`status-badge-${status}`}>
      {status === "active" && <Play className="w-3 h-3" />}
      {status === "paused" && <Pause className="w-3 h-3" />}
      {status === "completed" && <CheckCircle2 className="w-3 h-3" />}
      {status}
    </span>
  );
}

// ─── Empty States ─────────────────────────────────────────────

function EmptyExperiments({ onCreateClick }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-experiments">
      <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
        <FlaskConical className="w-8 h-8 text-zinc-500" />
      </div>
      <h3 className="text-lg font-medium text-zinc-200 mb-2">No experiments yet</h3>
      <p className="text-sm text-zinc-500 max-w-md mb-6">
        Create your first experiment to start comparing variants and measuring what converts best.
      </p>
      <Button onClick={onCreateClick} className="gap-2" data-testid="create-first-experiment-btn">
        <Plus className="w-4 h-4" /> Create Experiment
      </Button>
    </div>
  );
}

function EmptyVariants() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="empty-variants">
      <GitBranch className="w-8 h-8 text-zinc-600 mb-3" />
      <p className="text-sm text-zinc-500">No variants yet. Generate content from the Profit Path Builder to create trackable variants.</p>
    </div>
  );
}

function InsufficientData({ label }) {
  return (
    <div className="flex items-center gap-2 text-xs text-zinc-500" data-testid="insufficient-data">
      <AlertCircle className="w-3.5 h-3.5" />
      <span>{label || "Not enough data to calculate"}</span>
    </div>
  );
}

// ─── Variant Comparison Row ───────────────────────────────────

function EligibilityBadge({ eligibility }) {
  if (!eligibility) return null;
  const score = eligibility.confidence_score ?? 0;
  if (eligibility.eligible) {
    return (
      <Badge variant="outline" className="text-[10px] text-emerald-400 border-emerald-500/30 gap-1" data-testid="eligibility-badge-eligible">
        <ShieldCheck className="w-2.5 h-2.5" /> Eligible
      </Badge>
    );
  }
  const label = score < 0.5 ? "Low confidence" : "Building signal";
  const color = score < 0.5 ? "text-red-400 border-red-500/30" : "text-amber-400 border-amber-500/30";
  return (
    <Badge variant="outline" className={`text-[10px] ${color} gap-1`} data-testid="eligibility-badge-below">
      <ShieldAlert className="w-2.5 h-2.5" /> {label} ({Math.round(score * 100)}%)
    </Badge>
  );
}

function ConfidenceBar({ eligibility }) {
  if (!eligibility) return null;
  const score = Math.min(eligibility.confidence_score ?? 0, 1);
  const pct = Math.round(score * 100);
  const color = score >= 1 ? "bg-emerald-500" : score >= 0.5 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2 min-w-[120px]" data-testid="confidence-bar">
      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] font-mono text-zinc-400 w-8 text-right">{pct}%</span>
    </div>
  );
}

function VariantRow({ variant, isWinner, isWinnerDeclared }) {
  const ps = variant.performance_summary || {};
  const hasData = ps.impressions > 0 || ps.clicks > 0 || ps.leads > 0;
  const cr = ps.clicks > 0 ? ps.leads / ps.clicks : null;
  const rpl = ps.leads > 0 ? ps.revenue / ps.leads : null;

  return (
    <tr className={`border-b border-zinc-800/50 ${isWinner && isWinnerDeclared ? "bg-emerald-500/8" : isWinner ? "bg-emerald-500/5" : ""}`} data-testid={`variant-row-${variant.variant_id}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-zinc-400">{variant.variant_id}</span>
          {isWinner && isWinnerDeclared && (
            <Badge variant="outline" className="text-[10px] text-emerald-300 border-emerald-500/40 bg-emerald-500/10 gap-1 font-semibold" data-testid="winner-badge">
              <Trophy className="w-2.5 h-2.5" /> Winner
            </Badge>
          )}
          {isWinner && !isWinnerDeclared && <Badge variant="outline" className="text-[10px] text-emerald-400 border-emerald-500/30">Leader</Badge>}
          <EligibilityBadge eligibility={variant.eligibility} />
        </div>
        <div className="flex items-center gap-3 mt-1 text-[11px] text-zinc-500">
          <span>{variant.type}</span>
          {variant.messaging_hash && <span>hash: {variant.messaging_hash.slice(0, 8)}</span>}
          {variant.messaging_version && <span>v{variant.messaging_version}</span>}
        </div>
      </td>
      <td className="px-4 py-3 text-right font-mono text-sm text-zinc-300">{hasData ? formatMetric(ps.impressions) : "—"}</td>
      <td className="px-4 py-3 text-right font-mono text-sm text-zinc-300">{hasData ? formatMetric(ps.clicks) : "—"}</td>
      <td className="px-4 py-3 text-right font-mono text-sm text-zinc-300">{hasData ? formatMetric(ps.leads) : "—"}</td>
      <td className="px-4 py-3 text-right font-mono text-sm text-zinc-300">{hasData ? formatMetric(ps.appointments) : "—"}</td>
      <td className="px-4 py-3 text-right font-mono text-sm text-zinc-300">{hasData ? formatMetric(ps.revenue, "currency") : "—"}</td>
      <td className="px-4 py-3 text-right font-mono text-sm">
        {cr !== null ? <span className="text-zinc-200">{formatMetric(cr, "percent")}</span> : <span className="text-zinc-600">—</span>}
      </td>
      <td className="px-4 py-3 text-right font-mono text-sm">
        {rpl !== null ? <span className="text-emerald-400">{formatMetric(rpl, "currency")}</span> : <span className="text-zinc-600">—</span>}
      </td>
      <td className="px-4 py-3">
        <ConfidenceBar eligibility={variant.eligibility} />
      </td>
    </tr>
  );
}

// ─── Create Experiment Modal ─────────────────────────────────

function CreateExperimentModal({ variants, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("creative_test");
  const [selectedVids, setSelectedVids] = useState([]);
  const [creating, setCreating] = useState(false);

  const filteredVariants = variants.filter((v) => {
    const typeMap = { creative_test: "creative", funnel_test: "funnel", script_test: "script", landing_page_test: "landing_page" };
    return v.type === typeMap[type];
  });

  const toggleVariant = (vid) => {
    setSelectedVids((prev) => prev.includes(vid) ? prev.filter((v) => v !== vid) : [...prev, vid]);
  };

  const handleCreate = async () => {
    if (selectedVids.length < 2 || !name.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/performance/experiments`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ name, type, variant_ids: selectedVids }),
      });
      if (res.ok) {
        const data = await res.json();
        onCreated(data);
      }
    } catch (err) {
      console.error("Failed to create experiment:", err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="create-experiment-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-lg p-6 space-y-5">
        <h2 className="text-lg font-semibold text-zinc-100">Create Experiment</h2>

        <div>
          <label className="text-xs text-zinc-400 mb-1 block">Experiment Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-200 focus:outline-none focus:border-zinc-500"
            placeholder="e.g., Mortgage Protection Creative A/B"
            data-testid="experiment-name-input"
          />
        </div>

        <div>
          <label className="text-xs text-zinc-400 mb-1 block">Test Type</label>
          <select
            value={type}
            onChange={(e) => { setType(e.target.value); setSelectedVids([]); }}
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-200 focus:outline-none"
            data-testid="experiment-type-select"
          >
            <option value="creative_test">Creative Test</option>
            <option value="funnel_test">Funnel Test</option>
            <option value="script_test">Script Test</option>
            <option value="landing_page_test">Landing Page Test</option>
          </select>
        </div>

        <div>
          <label className="text-xs text-zinc-400 mb-1 block">Select Variants (min 2)</label>
          <div className="max-h-48 overflow-y-auto space-y-1.5 border border-zinc-800 rounded-lg p-2">
            {filteredVariants.length === 0 ? (
              <p className="text-xs text-zinc-500 py-4 text-center">No {type.replace("_test", "")} variants available. Generate content first.</p>
            ) : (
              filteredVariants.map((v) => (
                <label key={v.variant_id} className="flex items-center gap-3 p-2 rounded hover:bg-zinc-800 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={selectedVids.includes(v.variant_id)}
                    onChange={() => toggleVariant(v.variant_id)}
                    className="rounded border-zinc-600"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-xs text-zinc-300">{v.variant_id}</span>
                    <div className="text-[11px] text-zinc-500">
                      {v.messaging_hash && `hash: ${v.messaging_hash.slice(0, 8)}`}
                      {v.created_at && ` | ${new Date(v.created_at).toLocaleDateString()}`}
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onClose} data-testid="cancel-experiment-btn">Cancel</Button>
          <Button
            onClick={handleCreate}
            disabled={selectedVids.length < 2 || !name.trim() || creating}
            data-testid="confirm-create-experiment-btn"
          >
            {creating ? "Creating..." : "Create Experiment"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Decision Banner (MA-AI-401-1C+ Stopping Rules) ─────────

const DECISION_STATE_CONFIG = {
  insufficient_data: {
    label: "Insufficient Data",
    color: "border-zinc-700 bg-zinc-800/50",
    textColor: "text-zinc-400",
    icon: Clock,
    iconColor: "text-zinc-500",
  },
  testing: {
    label: "Testing",
    color: "border-zinc-600 bg-zinc-800/30",
    textColor: "text-zinc-300",
    icon: FlaskConical,
    iconColor: "text-zinc-400",
  },
  emerging: {
    label: "Emerging Signal",
    color: "border-blue-500/30 bg-blue-500/5",
    textColor: "text-blue-300",
    icon: TrendingUp,
    iconColor: "text-blue-400",
  },
  leading: {
    label: "Leader Emerging",
    color: "border-indigo-500/30 bg-indigo-500/8",
    textColor: "text-indigo-300",
    icon: TrendingUp,
    iconColor: "text-indigo-400",
  },
  winner_declared: {
    label: "Winner Declared",
    color: "border-emerald-500/30 bg-emerald-500/8",
    textColor: "text-emerald-300",
    icon: Trophy,
    iconColor: "text-emerald-400",
  },
  inconclusive: {
    label: "Inconclusive",
    color: "border-amber-500/30 bg-amber-500/5",
    textColor: "text-amber-300",
    icon: AlertCircle,
    iconColor: "text-amber-400",
  },
};

function DecisionBanner({ decision }) {
  if (!decision || !decision.decision_state) return null;

  const config = DECISION_STATE_CONFIG[decision.decision_state] || DECISION_STATE_CONFIG.testing;
  const Icon = config.icon;
  const metrics = decision.metrics || {};
  const thresholds = decision.thresholds || {};
  const isWinner = decision.decision_state === "winner_declared";

  return (
    <div className={`rounded-xl border-2 ${config.color} overflow-hidden`} data-testid="decision-banner">
      {/* Header */}
      <div className="px-5 py-4 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`mt-0.5 p-2 rounded-lg ${isWinner ? "bg-emerald-500/15" : "bg-zinc-800/80"}`}>
            <Icon className={`w-5 h-5 ${config.iconColor}`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className={`text-sm font-semibold ${config.textColor}`} data-testid="decision-state-label">
                {config.label}
              </span>
              {isWinner && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" data-testid="winner-lock-badge">
                  <Lock className="w-2.5 h-2.5" /> LOCKED
                </span>
              )}
              {decision.confidence_level > 0 && (
                <span className="text-[10px] font-mono text-zinc-500" data-testid="decision-confidence">
                  Confidence: {decision.confidence_level.toFixed(2)}
                </span>
              )}
            </div>
            <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed max-w-2xl" data-testid="decision-reasoning">
              {decision.reasoning}
            </p>
          </div>
        </div>

        {/* Action badge */}
        <div className="shrink-0">
          <span
            className={`inline-block px-3 py-1.5 text-[11px] font-medium rounded-lg ${
              decision.recommended_action === "declare_winner"
                ? "bg-emerald-500/15 text-emerald-300"
                : decision.recommended_action === "extend_test"
                ? "bg-amber-500/15 text-amber-300"
                : decision.recommended_action === "monitor_closely"
                ? "bg-indigo-500/15 text-indigo-300"
                : "bg-zinc-800 text-zinc-400"
            }`}
            data-testid="decision-action-badge"
          >
            {decision.recommended_action?.replace(/_/g, " ")}
          </span>
        </div>
      </div>

      {/* Metrics footer — only when we have comparison data */}
      {(metrics.delta !== null && metrics.delta !== undefined) && (
        <div className="px-5 py-3 border-t border-zinc-800/60 bg-zinc-900/30 flex flex-wrap items-center gap-x-6 gap-y-1.5 text-[11px] font-mono" data-testid="decision-metrics">
          {decision.winning_variant_id && (
            <span className="text-zinc-400">
              Leader: <span className="text-zinc-200">{decision.winning_variant_id}</span>
            </span>
          )}
          {decision.runner_up_variant_id && (
            <span className="text-zinc-400">
              Runner-up: <span className="text-zinc-200">{decision.runner_up_variant_id}</span>
            </span>
          )}
          {metrics.metric_used && (
            <span className="text-zinc-400">
              Metric: <span className="text-zinc-200">{metrics.metric_used}</span>
            </span>
          )}
          {metrics.delta != null && (
            <span className="text-zinc-400">
              Delta: <span className={`${metrics.delta >= 0.15 ? "text-emerald-400" : metrics.delta >= 0.05 ? "text-indigo-400" : "text-amber-400"}`}>
                {metrics.delta >= 0 ? "+" : ""}{(metrics.delta * 100).toFixed(1)}%
              </span>
            </span>
          )}
          {metrics.leader_rpl != null && (
            <span className="text-zinc-400">
              Leader {metrics.metric_used}: <span className="text-zinc-200">{metrics.metric_used === "RPL" ? `$${metrics.leader_rpl.toLocaleString()}` : `${(metrics.leader_cr * 100).toFixed(1)}%`}</span>
            </span>
          )}
          {metrics.runner_up_rpl != null && (
            <span className="text-zinc-400">
              Runner-up {metrics.metric_used}: <span className="text-zinc-200">{metrics.metric_used === "RPL" ? `$${metrics.runner_up_rpl.toLocaleString()}` : `${(metrics.runner_up_cr * 100).toFixed(1)}%`}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Allocation Recommendation Panel ─────────────────────────

function AllocationPanel({ experimentId, experimentStatus, onAllocationApplied, decisionState }) {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [fetchKey, setFetchKey] = useState(0);

  const isWinnerLocked = decisionState === "winner_declared";

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/performance/experiments/${experimentId}/recommendation`, { headers: getAuthHeaders() });
        if (res.ok) setRec(await res.json());
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [experimentId, fetchKey]);

  const handleApply = async () => {
    setApplying(true);
    setShowConfirm(false);
    try {
      const res = await fetch(`${API}/api/performance/experiments/${experimentId}/apply-allocation`, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        setFetchKey((k) => k + 1);
        onAllocationApplied();
      }
    } catch (err) { console.error(err); }
    finally { setApplying(false); }
  };

  if (loading) return null;
  if (!rec || !rec.recommended_allocation || Object.keys(rec.recommended_allocation).length === 0) return null;

  const meta = rec.meta || {};
  const isEvenSplit = meta.reason === "no_eligible_variants" || meta.reason === "no_performance_differentiation";
  const hasDelta = Object.values(rec.allocation_delta || {}).some((d) => Math.abs(d) > 0.001);
  const canApply = experimentStatus === "active" && hasDelta && !isWinnerLocked;
  const maxDelta = Math.max(...Object.values(rec.allocation_delta || {}).map(Math.abs));
  const isCloseCAll = hasDelta && maxDelta < 0.05;

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="allocation-panel">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-indigo-400" />
          <h3 className="text-sm font-medium text-zinc-200">Allocation Recommendation</h3>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-zinc-500 uppercase tracking-wide">
            {meta.metric_used === "revenue_per_lead" ? "By Rev/Lead" : meta.metric_used === "conversion_rate" ? "By CR" : "Even Split"}
          </span>
          {canApply && (
            <Button size="sm" onClick={() => setShowConfirm(true)} disabled={applying} data-testid="apply-allocation-btn">
              {applying ? "Applying..." : "Apply Allocation"}
            </Button>
          )}
          {isWinnerLocked && (
            <span className="inline-flex items-center gap-1.5 text-[10px] text-emerald-400 font-medium" data-testid="allocation-locked-label">
              <Lock className="w-3 h-3" /> Allocation Locked
            </span>
          )}
          {!hasDelta && experimentStatus === "active" && !isWinnerLocked && (
            <span className="text-[10px] text-emerald-400">Allocation is current</span>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="mx-4 mt-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5" data-testid="apply-confirm-dialog">
          <p className="text-sm text-amber-300 font-medium">Confirm allocation change</p>
          <p className="text-xs text-zinc-400 mt-1">This will shift traffic distribution across variants. Changes take effect immediately.</p>
          <div className="mt-2 text-xs text-zinc-500 space-y-0.5">
            {Object.entries(rec.recommended_allocation).map(([vid, w]) => {
              const cur = rec.current_allocation?.[vid] ?? 0;
              return <div key={vid}><span className="text-zinc-400">{vid}</span>: {(cur * 100).toFixed(0)}% → {(w * 100).toFixed(0)}%</div>;
            })}
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={handleApply} data-testid="confirm-apply-btn">Confirm</Button>
            <Button size="sm" variant="outline" onClick={() => setShowConfirm(false)} data-testid="cancel-apply-btn">Cancel</Button>
          </div>
        </div>
      )}
      <div className="p-4">
        {isEvenSplit ? (
          <p className="text-xs text-zinc-500" data-testid="allocation-even-split">
            {meta.reason === "no_eligible_variants"
              ? "No variants meet eligibility thresholds — allocation stays even."
              : "No performance differentiation between variants — allocation stays even."}
          </p>
        ) : (
          <div className="space-y-3">
            {isCloseCAll && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800/50 border border-zinc-700/50" data-testid="close-call-advisory">
                <AlertCircle className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                <span className="text-xs text-zinc-400">Variants are performing similarly (delta &lt; 5%). Consider gathering more data before shifting allocation.</span>
              </div>
            )}
            {Object.entries(rec.recommended_allocation).map(([vid, recWeight]) => {
              const curWeight = rec.current_allocation?.[vid] ?? 0;
              const delta = rec.allocation_delta?.[vid] ?? 0;
              const reasoning = rec.variant_reasoning?.[vid] || "";
              const deltaColor = delta > 0.001 ? "text-emerald-400" : delta < -0.001 ? "text-red-400" : "text-zinc-500";
              const deltaSign = delta > 0 ? "+" : "";
              return (
                <div key={vid} className="flex items-center gap-4" data-testid={`allocation-row-${vid}`}>
                  <span className="font-mono text-xs text-zinc-400 w-32 shrink-0 truncate">{vid}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm font-mono text-zinc-500 w-12 text-right">{(curWeight * 100).toFixed(0)}%</span>
                    <ArrowRight className="w-3.5 h-3.5 text-zinc-600" />
                    <span className="text-sm font-mono text-zinc-100 font-semibold w-12 text-right">{(recWeight * 100).toFixed(0)}%</span>
                  </div>
                  <span className={`text-xs font-mono w-12 ${deltaColor}`}>{deltaSign}{(delta * 100).toFixed(0)}%</span>
                  <div className="flex-1 min-w-0">
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${recWeight * 100}%` }} />
                    </div>
                  </div>
                  <span className="text-[11px] text-zinc-500 shrink-0 max-w-[200px] truncate">{reasoning}</span>
                </div>
              );
            })}
          </div>
        )}
        {/* Meta footer */}
        <div className="mt-3 pt-3 border-t border-zinc-800/50 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-zinc-500" data-testid="allocation-meta">
          <span>Floor: {(meta.exploration_floor * 100).toFixed(0)}%</span>
          {meta.confidence_weighting_applied && <span>Confidence: {(meta.confidence_factor * 100).toFixed(0)}%</span>}
          <span>Reason: {meta.reason?.replace(/_/g, " ")}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Experiment Detail Panel ─────────────────────────────────

function ExperimentDetail({ experiment, onStatusChange, onBack, onRefresh }) {
  const [decision, setDecision] = useState(null);
  const vds = experiment.variant_details || [];
  const hasEvents = vds.some((v) => (v.performance_summary?.impressions || 0) > 0);

  // Fetch decision from recommendation endpoint
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/performance/experiments/${experiment.experiment_id}/recommendation`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setDecision(data.decision || null);
        }
      } catch (err) { console.error(err); }
    })();
  }, [experiment.experiment_id, experiment]);

  const decisionState = decision?.decision_state;
  const isWinnerDeclared = decisionState === "winner_declared";
  const winnerVid = decision?.winning_variant_id;

  // Find the "leader" by revenue_per_lead (fallback when no decision)
  let leaderVid = winnerVid;
  if (!leaderVid && hasEvents) {
    let bestRpl = -1;
    for (const v of vds) {
      const ps = v.performance_summary || {};
      const rpl = ps.leads > 0 ? ps.revenue / ps.leads : 0;
      if (rpl > bestRpl) { bestRpl = rpl; leaderVid = v.variant_id; }
    }
  }

  // Aggregate totals
  const totals = vds.reduce((acc, v) => {
    const ps = v.performance_summary || {};
    acc.impressions += ps.impressions || 0;
    acc.clicks += ps.clicks || 0;
    acc.leads += ps.leads || 0;
    acc.appointments += ps.appointments || 0;
    acc.sales += ps.sales || 0;
    acc.revenue += ps.revenue || 0;
    return acc;
  }, { impressions: 0, clicks: 0, leads: 0, appointments: 0, sales: 0, revenue: 0 });

  const eligSummary = experiment.eligibility_summary;

  return (
    <div className="space-y-6" data-testid="experiment-detail">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={onBack} className="text-xs text-zinc-500 hover:text-zinc-300 mb-1">&larr; Back to experiments</button>
          <h2 className="text-xl font-semibold text-zinc-100">{experiment.name}</h2>
          <div className="flex items-center gap-3 mt-1">
            <StatusBadge status={experiment.status} />
            <span className="text-xs text-zinc-500">{experiment.type.replace("_", " ")}</span>
            <span className="text-xs text-zinc-500">{vds.length} variants</span>
          </div>
        </div>
        <div className="flex gap-2">
          {experiment.status === "active" && !isWinnerDeclared && (
            <Button size="sm" variant="outline" onClick={() => onStatusChange("paused")} data-testid="pause-experiment-btn">
              <Pause className="w-3.5 h-3.5 mr-1.5" /> Pause
            </Button>
          )}
          {experiment.status === "paused" && (
            <Button size="sm" variant="outline" onClick={() => onStatusChange("active")} data-testid="resume-experiment-btn">
              <Play className="w-3.5 h-3.5 mr-1.5" /> Resume
            </Button>
          )}
          {experiment.status !== "completed" && !isWinnerDeclared && (
            <Button size="sm" variant="outline" onClick={() => onStatusChange("completed")} data-testid="complete-experiment-btn">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Complete
            </Button>
          )}
          {isWinnerDeclared && experiment.status !== "completed" && (
            <Button size="sm" onClick={() => onStatusChange("completed")} data-testid="finalize-experiment-btn">
              <Trophy className="w-3.5 h-3.5 mr-1.5" /> Finalize Winner
            </Button>
          )}
        </div>
      </div>

      {/* Decision Banner (Stopping Rules) */}
      {decision && <DecisionBanner decision={decision} />}

      {/* Eligibility Gate Banner */}
      {eligSummary && (
        <div
          className={`flex items-center justify-between rounded-lg border px-4 py-3 ${
            eligSummary.all_eligible
              ? "border-emerald-500/20 bg-emerald-500/5"
              : "border-amber-500/20 bg-amber-500/5"
          }`}
          data-testid="eligibility-gate-banner"
        >
          <div className="flex items-center gap-3">
            {eligSummary.all_eligible ? (
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
            ) : (
              <ShieldAlert className="w-5 h-5 text-amber-400" />
            )}
            <div>
              <p className={`text-sm font-medium ${eligSummary.all_eligible ? "text-emerald-300" : "text-amber-300"}`}>
                {eligSummary.all_eligible
                  ? "All variants eligible for optimization"
                  : `${eligSummary.eligible_variants}/${eligSummary.total_variants} variants eligible`}
              </p>
              <p className="text-xs text-zinc-500 mt-0.5">
                Thresholds: {Object.entries(eligSummary.thresholds || {}).map(([k, v]) => `${k} >= ${v}`).join(", ")}
              </p>
            </div>
          </div>
          {!eligSummary.all_eligible && (
            <span className="text-xs text-amber-400/80">Traffic stays evenly distributed until all variants meet thresholds</span>
          )}
        </div>
      )}

      {/* Aggregate Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <MetricCard label="Impressions" value={totals.impressions || null} testId="metric-impressions" />
        <MetricCard label="Clicks" value={totals.clicks || null} testId="metric-clicks" />
        <MetricCard label="Leads" value={totals.leads || null} testId="metric-leads" />
        <MetricCard label="Appointments" value={totals.appointments || null} testId="metric-appointments" />
        <MetricCard label="Sales" value={totals.sales || null} testId="metric-sales" />
        <MetricCard label="Revenue" value={totals.revenue || null} type="currency" testId="metric-revenue" />
        <MetricCard label="Rev/Lead" value={totals.leads > 0 ? totals.revenue / totals.leads : null} type="currency" testId="metric-rpl" />
      </div>

      {/* Variant Comparison Table */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-sm font-medium text-zinc-200">Variant Comparison</h3>
          {!hasEvents && <InsufficientData label="No performance data yet" />}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="variant-comparison-table">
            <thead>
              <tr className="border-b border-zinc-800 text-xs text-zinc-500 uppercase tracking-wide">
                <th className="px-4 py-2.5 text-left">Variant</th>
                <th className="px-4 py-2.5 text-right">Imp</th>
                <th className="px-4 py-2.5 text-right">Clicks</th>
                <th className="px-4 py-2.5 text-right">Leads</th>
                <th className="px-4 py-2.5 text-right">Appts</th>
                <th className="px-4 py-2.5 text-right">Revenue</th>
                <th className="px-4 py-2.5 text-right">CR</th>
                <th className="px-4 py-2.5 text-right">Rev/Lead</th>
                <th className="px-4 py-2.5 text-left">Confidence</th>
              </tr>
            </thead>
            <tbody>
              {vds.map((v) => (
                <VariantRow key={v.variant_id} variant={v} isWinner={v.variant_id === leaderVid && hasEvents} isWinnerDeclared={isWinnerDeclared} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Allocation Recommendation */}
      <AllocationPanel
        experimentId={experiment.experiment_id}
        experimentStatus={experiment.status}
        onAllocationApplied={onRefresh}
        decisionState={decisionState}
      />

      {/* Variant Lineage */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <h3 className="text-sm font-medium text-zinc-200 mb-3">Variant Lineage</h3>
        <div className="space-y-3">
          {vds.map((v) => (
            <div key={v.variant_id} className="p-3 bg-zinc-800/50 rounded-lg text-xs" data-testid={`lineage-${v.variant_id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-zinc-300">{v.variant_id}</span>
                <span className="text-zinc-500">{v.type}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-zinc-400">
                <div><span className="text-zinc-500">Created: </span>{v.created_at ? new Date(v.created_at).toLocaleDateString() : "—"}</div>
                <div><span className="text-zinc-500">Msg Version: </span>{v.messaging_version ?? "—"}</div>
                <div><span className="text-zinc-500">Msg Hash: </span>{v.messaging_hash ? v.messaging_hash.slice(0, 12) : "—"}</div>
                <div><span className="text-zinc-500">Path: </span>{v.profit_path_id || "—"}</div>
              </div>
              {v.strategy_signals_snapshot && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(v.strategy_signals_snapshot)
                    .filter(([k, val]) => typeof val === "string" || typeof val === "number" || typeof val === "boolean")
                    .map(([k, val]) => (
                    <span key={k} className="px-1.5 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-400">
                      {k}: {String(val)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export default function ExperimentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [experiments, setExperiments] = useState([]);
  const [variants, setVariants] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedExp, setSelectedExp] = useState(null);
  const [activeTab, setActiveTab] = useState("experiments");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [expRes, varRes, metRes, sigRes] = await Promise.all([
        fetch(`${API}/api/performance/experiments`, { headers }),
        fetch(`${API}/api/performance/variants?limit=100`, { headers }),
        fetch(`${API}/api/performance/metrics?scope=campaign&group_by=variant`, { headers }),
        fetch(`${API}/api/performance/signals`, { headers }),
      ]);
      if (expRes.ok) setExperiments((await expRes.json()).experiments || []);
      if (varRes.ok) setVariants((await varRes.json()).variants || []);
      if (metRes.ok) setMetrics(await metRes.json());
      if (sigRes.ok) setSignals((await sigRes.json()).summaries || []);
    } catch (err) {
      console.error("Failed to fetch experiments data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-open experiment from notification link (?open=experiment_id)
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && !loading && !selectedExp) {
      handleSelectExperiment(openId);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, loading]);

  const handleSelectExperiment = async (expId) => {
    try {
      const res = await fetch(`${API}/api/performance/experiments/${expId}`, { headers: getAuthHeaders() });
      if (res.ok) setSelectedExp(await res.json());
    } catch (err) { console.error(err); }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedExp) return;
    try {
      const res = await fetch(`${API}/api/performance/experiments/${selectedExp.experiment_id}`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        const updated = await res.json();
        setSelectedExp({ ...selectedExp, ...updated });
        fetchAll();
      }
    } catch (err) { console.error(err); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="experiments-loading">
        <div className="animate-spin w-8 h-8 border-2 border-zinc-600 border-t-zinc-300 rounded-full" />
      </div>
    );
  }

  if (selectedExp) {
    const refreshExperiment = async () => {
      try {
        const res = await fetch(`${API}/api/performance/experiments/${selectedExp.experiment_id}`, { headers: getAuthHeaders() });
        if (res.ok) setSelectedExp(await res.json());
      } catch (err) { console.error(err); }
    };
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <ExperimentDetail
          experiment={selectedExp}
          onStatusChange={handleStatusChange}
          onBack={() => { setSelectedExp(null); fetchAll(); }}
          onRefresh={refreshExperiment}
        />
      </div>
    );
  }

  const totalMetrics = metrics?.metrics || {};

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 space-y-8" data-testid="experiments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-100">Experiments</h1>
          <p className="text-sm text-zinc-500 mt-1">Test, measure, and optimize your marketing assets</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2" data-testid="create-experiment-btn">
          <Plus className="w-4 h-4" /> New Experiment
        </Button>
      </div>

      {/* Overview Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <MetricCard label="Impressions" value={totalMetrics.impressions || null} testId="total-impressions" />
        <MetricCard label="Clicks" value={totalMetrics.clicks || null} testId="total-clicks" />
        <MetricCard label="Leads" value={totalMetrics.leads || null} testId="total-leads" />
        <MetricCard label="Appointments" value={totalMetrics.appointments || null} testId="total-appointments" />
        <MetricCard label="Revenue" value={totalMetrics.revenue || null} type="currency" testId="total-revenue" />
        <MetricCard label="CR" value={totalMetrics.conversion_rate} type="percent" testId="total-cr" />
        <MetricCard label="Rev/Lead" value={totalMetrics.revenue_per_lead} type="currency" testId="total-rpl" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-800 pb-px">
        {[
          { id: "experiments", label: "Experiments", icon: FlaskConical },
          { id: "variants", label: "All Variants", icon: GitBranch },
          { id: "signals", label: "Signal Insights", icon: TrendingUp },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === tab.id
                ? "text-zinc-100 bg-zinc-800/50 border-b-2 border-zinc-300"
                : "text-zinc-500 hover:text-zinc-300"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "experiments" && (
        experiments.length === 0 ? (
          <EmptyExperiments onCreateClick={() => setShowCreate(true)} />
        ) : (
          <div className="space-y-3" data-testid="experiments-list">
            {experiments.map((exp) => (
              <button
                key={exp.experiment_id}
                onClick={() => handleSelectExperiment(exp.experiment_id)}
                className="w-full text-left bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors"
                data-testid={`experiment-card-${exp.experiment_id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-zinc-200">{exp.name}</h3>
                    <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                      <span>{exp.type.replace("_", " ")}</span>
                      <span>{exp.variants?.length || 0} variants</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(exp.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <StatusBadge status={exp.status} />
                </div>
              </button>
            ))}
          </div>
        )
      )}

      {activeTab === "variants" && (
        variants.length === 0 ? (
          <EmptyVariants />
        ) : (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="all-variants-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800 text-xs text-zinc-500 uppercase tracking-wide">
                    <th className="px-4 py-2.5 text-left">Variant</th>
                    <th className="px-4 py-2.5 text-left">Type</th>
                    <th className="px-4 py-2.5 text-left">Created</th>
                    <th className="px-4 py-2.5 text-left">Msg Hash</th>
                    <th className="px-4 py-2.5 text-right">Imp</th>
                    <th className="px-4 py-2.5 text-right">Leads</th>
                    <th className="px-4 py-2.5 text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {variants.map((v) => {
                    const ps = v.performance_summary || {};
                    return (
                      <tr key={v.variant_id} className="border-b border-zinc-800/50" data-testid={`all-variant-row-${v.variant_id}`}>
                        <td className="px-4 py-3 font-mono text-xs text-zinc-300">{v.variant_id}</td>
                        <td className="px-4 py-3 text-xs text-zinc-400">{v.type}</td>
                        <td className="px-4 py-3 text-xs text-zinc-500">{v.created_at ? new Date(v.created_at).toLocaleDateString() : "—"}</td>
                        <td className="px-4 py-3 font-mono text-xs text-zinc-500">{v.messaging_hash?.slice(0, 10) || "—"}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-zinc-300">{ps.impressions || "—"}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-zinc-300">{ps.leads || "—"}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-zinc-300">{ps.revenue ? formatMetric(ps.revenue, "currency") : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {activeTab === "signals" && (
        signals.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-signals">
            <TrendingUp className="w-8 h-8 text-zinc-600 mb-3" />
            <p className="text-sm text-zinc-500 mb-4">No signal insights yet. Collect performance data and compute correlations.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                await fetch(`${API}/api/performance/signals/compute`, { method: "POST", headers: getAuthHeaders() });
                fetchAll();
              }}
              data-testid="compute-signals-btn"
            >
              <BarChart3 className="w-4 h-4 mr-1.5" /> Compute Insights
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="signal-insights-list">
            {signals.map((s, i) => (
              <div key={i} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.entries(s.signal_combination || {})
                    .filter(([k, val]) => typeof val === "string" || typeof val === "number" || typeof val === "boolean")
                    .map(([k, val]) => (
                    <span key={k} className="px-2 py-1 bg-zinc-800 rounded text-xs text-zinc-300">
                      {k}: <span className="text-zinc-100 font-medium">{String(val)}</span>
                    </span>
                  ))}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><span className="text-zinc-500 text-xs">Sample Size</span><p className="text-zinc-200 font-mono">{s.sample_size}</p></div>
                  <div><span className="text-zinc-500 text-xs">Avg CR</span><p className="text-zinc-200 font-mono">{s.avg_conversion_rate != null ? formatMetric(s.avg_conversion_rate, "percent") : "—"}</p></div>
                  <div><span className="text-zinc-500 text-xs">Avg Rev/Lead</span><p className="text-emerald-400 font-mono">{s.avg_revenue_per_lead != null ? formatMetric(s.avg_revenue_per_lead, "currency") : "—"}</p></div>
                  <div><span className="text-zinc-500 text-xs">Avg Rev/Appt</span><p className="text-zinc-200 font-mono">{s.avg_revenue_per_appointment != null ? formatMetric(s.avg_revenue_per_appointment, "currency") : "—"}</p></div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Create Modal */}
      {showCreate && (
        <CreateExperimentModal
          variants={variants}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchAll(); }}
        />
      )}
    </div>
  );
}
